// src/components/Step5.tsx
import { Button, Center, Group, Title, Text, Flex, Space, TextInput, Badge } from "@mantine/core";
import { ValuesContext } from "../../ValuesContext";
import { useContext, useState } from "react";
import useDocumentTitle from "../../useDocumentTitle";
import datesPriceDataProvider from "../../DataProvider/datesPriceDataProvider";
import { TotalPrice, applyDiscountToNights, buildTimeUnitPrices } from "./utils";

type ChargingMode =
  | "PerPersonPerTimeUnit"
  | "PerTimeUnit"
  | "PerPerson"
  | "Once"
  | string;

// --- helpers d'affichage ---
const fmtEUR = (n: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + "€";

// Reconnaît "Repas package" / "Petit-déjeuner package" (variantes et accents)
const isRepasPackage = (name: string) =>
  /package/i.test(name) && /(repas|déj|dejeuner|petit.?déj)/i.test(name);

const Step5 = (props: {
  active: number;
  night: number; // nb de nuits
  handleStepChange(nextStep: number): any;
}) => {
  const { values, setValues } = useContext(ValuesContext);

  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ code: string; percent: number } | null>(null);

  useDocumentTitle(`Recapitulatif de la réservation - ${values.Hotel.name}`);

  // ——— règles de personnes : 2 si (2 curistes) ou (1 curiste + 1 accompagnant)
  const personsCount =
    values.Step1.NbCuriste !== "1" || Number(values.Step1.Accompagnant) === 1 ? 2 : 1;

  // % de promo global
  const discountPct = Number(values?.Discount) || 0;
  const discountFactor = 1 - discountPct / 100;

  // ------- total d’une ligne d’option (Count est pris en compte ici, pas ailleurs)
  const getAdditionalProductTotal = (product: {
    chargingMode: ChargingMode;
    price: number;
    Count?: number;
  }) => {
    const count = Number(product.Count ?? 1);
    switch (product.chargingMode) {
      case "PerPersonPerTimeUnit":
        return product.price * personsCount * props.night * count;
      case "PerTimeUnit":
        return product.price * props.night * count;
      case "PerPerson":
        return product.price * personsCount * count;
      case "Once":
      default:
        return product.price * count;
    }
  };

  const recomputeAdditionalProductsPrice = (additional: any[]) => {
    const total = (additional || []).reduce(
      (acc, p) =>
        acc +
        getAdditionalProductTotal({
          chargingMode: p.chargingMode,
          price: Number(p.price) || 0,
          Count: Number(p.Count ?? 1),
        }),
      0
    );
    return Number.isFinite(total) ? Number(total.toFixed(2)) : 0;
  };

  // ——— helper générique : insérer/mettre à jour UNE SEULE ligne promo (Count = 1)
  const upsertSinglePromo = (
    current: any[],
    prod: { Id: string; ExternalName: string; ChargingMode?: ChargingMode }
  ) => {
    if (!prod?.Id) return current;
    const next = (current || []).filter(
      (p: any) => p?.ProductName !== prod.ExternalName && p?.ProductId !== prod.Id
    );
    next.push({
      ProductId: prod.Id,
      ProductName: prod.ExternalName,
      price: 0,
      chargingMode: prod.ChargingMode || "PerPersonPerTimeUnit",
      Count: 1,
    });
    return next;
  };

  // ——— petit-déj : 1 seule ligne, Count = 1
  const ensureBreakfastSingle = (current: any[], mewsBreakfastId: string | null) => {
    if (!mewsBreakfastId) return current;
    return upsertSinglePromo(current, {
      Id: mewsBreakfastId,
      ExternalName: "Remise petit-déjeuner",
      ChargingMode: "PerPersonPerTimeUnit",
    });
  };

  // ——— repas : 1 seule ligne, Count = 1
  const ensureMealSingle = (current: any[], mewsMealId: string | null) => {
    if (!mewsMealId) return current;
    return upsertSinglePromo(current, {
      Id: mewsMealId,
      ExternalName: "Remise repas",
      ChargingMode: "PerPersonPerTimeUnit",
    });
  };

  // ——— (optionnel) IDs MEWS export : une seule occurrence chacun
  const buildMewsPromoIds = (mewsBreakfastId: string | null, mewsMealId: string | null) => {
    return {
      breakfast: mewsBreakfastId ? [mewsBreakfastId] : [],
      meal: mewsMealId ? [mewsMealId] : [],
    };
  };

  // ——— util local pour retrouver le tableau de prix de chambre brut selon la config
  const resolveBaseNightPrices = (v: any): number[] => {
    const sel = v?.Step3?.selectedRoom?.price;
    if (!sel) return Array.isArray(v?.Step3?.Price) ? v.Step3.Price : [];
    if (v.Step1.NbCuriste === "2" && Number(v.Step1.Accompagnant) === 0) {
      return sel.price_two_curists || [];
    } else if (v.Step1.NbCuriste === "1" && Number(v.Step1.Accompagnant) === 1) {
      return sel.price_curist_companion || [];
    } else if (v.Step1.NbCuriste === "1" && Number(v.Step1.Accompagnant) === 0) {
      return sel.prices_single || [];
    }
    return v?.Step3?.Price || [];
  };

  const getStayStartDate = (v: any): Date | null => {
    const raw =
      v?.Date?.StartUtc ??
      v?.Date?.Startutc ??
      null;

    if (!raw) return null;

    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };


  const isStayInBlackFridayWindows = (v: any): boolean => {
    const stayStart = getStayStartDate(v);
    if (!stayStart) return false;

    stayStart.setHours(0, 0, 0, 0);

    const start = new Date("2026-06-26T00:00:00"); // inclus
    const endExclusive = new Date("2026-07-31T00:00:00"); // 04/05 inclus

    start.setHours(0, 0, 0, 0);
    endExclusive.setHours(0, 0, 0, 0);

    return stayStart >= start && stayStart < endExclusive;
  };


  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      setPromoError("Merci de saisir un code.");
      return;
    }
    setPromoLoading(true);
    setPromoError(null);

    try {
      const hotelId = Number(values?.Hotel?.id);
      const resp = await datesPriceDataProvider.getDiscountCodeByCode(code, hotelId);

      if (!resp?.valid || !resp?.discount) {
        setApplied(null);
        setPromoError(resp?.message ?? "Code invalide ou non applicable.");
        setValues((prev: any) => {
          const cleanedAdditional = (prev.AdditionalProducts || []).filter(
            (p: any) =>
              !["Remise petit-déjeuner", "Remise repas"].includes(p?.ProductName)
          );
          const next: any = {
            ...prev,
            Discount: 0,
            DiscountCode: undefined,
            DiscountMeta: undefined,
            discountmews: "",
            PromoBreakfastMewsId: null,
            PromoMealMewsId: null,
            AdditionalProducts: cleanedAdditional,
            Restauration: {
              ...prev.Restauration,
              PromoBreakfastMewsId: null,
              PromoMealMewsId: null,
            },
            MewsPromoProductIds: { breakfast: [], meal: [] as string[] },
          };
          next.Step3 = { ...prev.Step3, DiscountedRoomPrices: undefined };
          next.TimeUnitPricesDiscounted = undefined;

          next.AdditionalProductsPrice = recomputeAdditionalProductsPrice(cleanedAdditional);
          return { ...next, TotalPrice: TotalPrice(next) };
        });
        return;
      }

      const d = resp.discount;

      // --- contrôle des dates de séjour pour l'OP Black Friday / Cyber Monday ---
      if (!isStayInBlackFridayWindows(values)) {
        setApplied(null);
        const PROMO_START = "26/06/2026";
        const PROMO_END = "31/07/2026";

        setPromoError(
          `Ce code est valable pour les séjours du ${PROMO_START} au ${PROMO_END} inclus.`
        );


        // Même nettoyage que pour un code invalide
        setValues((prev: any) => {
          const cleanedAdditional = (prev.AdditionalProducts || []).filter(
            (p: any) =>
              !["Remise petit-déjeuner", "Remise repas"].includes(p?.ProductName)
          );
          const next: any = {
            ...prev,
            Discount: 0,
            DiscountCode: undefined,
            DiscountMeta: undefined,
            discountmews: "",
            PromoBreakfastMewsId: null,
            PromoMealMewsId: null,
            AdditionalProducts: cleanedAdditional,
            Restauration: {
              ...prev.Restauration,
              PromoBreakfastMewsId: null,
              PromoMealMewsId: null,
            },
            MewsPromoProductIds: { breakfast: [], meal: [] as string[] },
          };
          next.Step3 = { ...prev.Step3, DiscountedRoomPrices: undefined };
          next.TimeUnitPricesDiscounted = undefined;
          next.AdditionalProductsPrice =
            recomputeAdditionalProductsPrice(cleanedAdditional);
          return { ...next, TotalPrice: TotalPrice(next) };
        });

        return; // on arrête là : code non applicable au séjour
      }

      const percent =
        Number(d.dicount_percentage ?? d.discount_percentage ?? d.amount ?? 0) || 0;
      if (percent <= 0) throw new Error("Ce code ne contient aucune remise.");

      const discountMewsId = d.mewsId ?? d.mewsid ?? d.mews_id ?? "";
      const mewsBreakfastId: string | null = d.mewsBreakfastId ?? d.mews_breakfast_id ?? null;
      const mewsMealId: string | null = d.mewsMealId ?? d.mews_meal_id ?? null;

      setValues((prev: any) => {
        let next: any = {
          ...prev,
          Discount: percent,
          DiscountCode: d.code ?? code,
          DiscountMeta: {
            id: d.id,
            hotel_id: d.hotel_id,
            startAt: d.startAt,
            endAt: d.endAt,
            mewsId: discountMewsId,
            mewsMealId,
            mewsBreakfastId,
          },
          discountmews: discountMewsId,
          PromoBreakfastMewsId: mewsBreakfastId,
          PromoMealMewsId: mewsMealId,
          Restauration: {
            ...prev.Restauration,
            PromoBreakfastMewsId: mewsBreakfastId ?? null,
            PromoMealMewsId: mewsMealId ?? null,
          },
        };

        // — LIGNES D’OPTIONS : 1 seule de chaque, Count = 1
        let additional: any[] = Array.isArray(prev.AdditionalProducts) ? [...prev.AdditionalProducts] : [];
        additional = additional.filter(
          (p) => !["Remise petit-déjeuner", "Remise repas"].includes(p?.ProductName)
        );

        additional = ensureBreakfastSingle(additional, mewsBreakfastId);
        additional = ensureMealSingle(additional, mewsMealId);

        // — prix de chambre remisés (pour affichage + MEWS)
        const baseNightPrices: number[] = resolveBaseNightPrices(prev);
        const roomPricesDiscounted = applyDiscountToNights(baseNightPrices, percent);

        next.Step3 = {
          ...prev.Step3,
          Price: baseNightPrices,
          DiscountedRoomPrices: roomPricesDiscounted,
        };
        next.TimeUnitPricesDiscounted = buildTimeUnitPrices(roomPricesDiscounted);

        // — IDs MEWS export (1 seul de chaque si besoin)
        next.MewsPromoProductIds = buildMewsPromoIds(mewsBreakfastId, mewsMealId);

        next.AdditionalProducts = additional;
        next.AdditionalProductsPrice = recomputeAdditionalProductsPrice(additional);
        next.TotalPrice = TotalPrice(next);
        return next;
      });

      setApplied({ code: d.code ?? code, percent });
      setPromoError(null);
    } catch (e: any) {
      setApplied(null);
      setPromoError(e?.message || "Erreur réseau. Réessaie.");
      setValues((prev: any) => {
        const cleanedAdditional = (prev.AdditionalProducts || []).filter(
          (p: any) => !["Remise petit-déjeuner", "Remise repas"].includes(p?.ProductName)
        );
        const next: any = {
          ...prev,
          Discount: 0,
          DiscountCode: undefined,
          DiscountMeta: undefined,
          discountmews: "",
          PromoBreakfastMewsId: null,
          PromoMealMewsId: null,
          AdditionalProducts: cleanedAdditional,
          Restauration: {
            ...prev.Restauration,
            PromoBreakfastMewsId: null,
            PromoMealMewsId: null,
          },
          MewsPromoProductIds: { breakfast: [], meal: [] as string[] },
        };
        next.Step3 = { ...prev.Step3, DiscountedRoomPrices: undefined };
        next.TimeUnitPricesDiscounted = undefined;
        next.AdditionalProductsPrice = recomputeAdditionalProductsPrice(cleanedAdditional);
        return { ...next, TotalPrice: TotalPrice(next) };
      });
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="select-zone">
      <Center>
        <Title order={3}>Récapitulatif</Title>
        <Space h="xl" />
        <Space h="xl" />
      </Center>

      <Title order={3}>Code promotionnel</Title>
      <Flex gap="sm" align="end" mb="lg">
        <TextInput
          label="Code"
          placeholder="Ex: RENTREE25"
          value={promoInput}
          onChange={(e) => setPromoInput(e.currentTarget.value)}
          style={{ maxWidth: 260 }}
        />
        <Button loading={promoLoading} onClick={applyPromo} className={"night-nav-active"}>
          Appliquer
        </Button>
        {applied && (
          <Badge color="green" variant="light">
            {applied.code} appliqué {applied.percent ? `(${applied.percent}%)` : ""}
          </Badge>
        )}
      </Flex>
      {promoError && (
        <Text color="red" size="sm" mt="xs">
          {promoError}
        </Text>
      )}

      <Title order={3}>Participants</Title>
      <Text className={"main-detail"}>{values.Step1.Label}</Text>
      <Text>{values.Step1.Description}</Text>

      <Title order={3}>Programmes des soins</Title>
      <Flex justify={"space-between"}>
        <Text className={"main-detail"}>Curiste 1 : {values.CureName}</Text>
      </Flex>
      {Number(values.Step2.Value) > 0 && (
        <Flex justify={"space-between"}>
          <Text className={"main-detail"}>
            Curiste 2 : {values.Step2.CureName}
          </Text>
        </Flex>
      )}

      {values.Hotel.id !== 1 && (
        <>
          <Title order={3}>Votre Chambre</Title>
          <Flex justify={"space-between"}>
            <Text className={"main-detail"}>{values.Step3.RoomName}</Text>
          </Flex>

          <Title order={3}>Restauration</Title>
          <Flex justify={"space-between"}>
            <Text className={"main-detail"}>{values.Restauration.ProductName}</Text>
          </Flex>

          <Title order={3}>Options</Title>
          {(values.AdditionalProducts || []).map((product: any, idx: number) => {
            const name = String(product.ProductName || "");
            const baseTotal = getAdditionalProductTotal({
              chargingMode: product.chargingMode,
              price: Number(product.price) || 0,
              Count: Number(product.Count ?? 1),
            });
            const isMealPkg = isRepasPackage(name);
            const showDiscount = isMealPkg && discountPct > 0;
            const discountedTotal = showDiscount ? Number((baseTotal * discountFactor).toFixed(2)) : baseTotal;

            return (
              <Flex key={idx} justify={"space-between"}>
                <Text className={"main-detail"}>
                  {(product.Count ?? 1)} {name}
                </Text>
                <Text className={"main-detail"}>
                  {showDiscount ? (
                    <>
                      <span style={{ textDecoration: "line-through", marginRight: 8 }}>
                        {fmtEUR(baseTotal)}
                      </span>
                      <strong>{fmtEUR(discountedTotal)}</strong>
                    </>
                  ) : (
                    <strong>{fmtEUR(baseTotal)}</strong>
                  )}
                </Text>
              </Flex>
            );
          })}
        </>
      )}

      <Space h="xl" />
      <Center>
        30% d’arrhes vous sera demandé pour confirmer votre séjour sous 24 heures.
      </Center>
      <Center>Sans cela, la réservation sera automatiquement annulée.</Center>

      <Group position="center" my="xl">
        <Button
          className={"step-btn-previous"}
          onClick={() => {
            if (values.Hotel.id === 1) {
              props.handleStepChange(props.active - 3);
            } else {
              props.handleStepChange(props.active - 1);
            }
          }}
        >
          ← Retour
        </Button>
        <Button
          className={"night-nav-active"}
          onClick={() => props.handleStepChange(props.active + 1)}
        >
          Suivant →
        </Button>
      </Group>
    </div>
  );
};

export default Step5;
